/// <amd-module name="TFS/ThemeManagement/Components/ThemePanel/ThemePanel" />
import * as React from "react";

import { expandThemeableProperties } from "VSS/Core/Util/Theming";
import { css, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { ClientTheme } from "VSS/Platform/Theming";

import { ContentSize } from "VSSUI/Callout";
import { FocusGroupContext } from "VSSUI/FocusGroup";
import { FocusZone, FocusZoneContext, FocusZoneDirection } from "VSSUI/FocusZone";
import { Link } from "VSSUI/Link";
import { Panel } from "VSSUI/Panel";
import { KeyCode } from "VSSUI/Util";
import { getTabIndex } from "VSSUI/Utilities/Focus";

import * as ThemeResources from "TFS/ThemeManagement/Resources";
import { IThemeChangedEvent } from "../../TfsThemeService";
import { ThemePanelProps } from "./ThemePanel.Props";

export interface IThemePanelState {
    themeGroups?: { [groupName: string]: ClientTheme[] };
    currentThemeId?: string;
    additionalThemesUrl?: string;
}

function sanitizeThemeId(id: string) {
    return id.replace(/\./g, "-");
}

/**
 * The FeatuerManagementPanelComponent is used to show a set of features based on feature
 * scopes. The current user will be able to see all the values, and depending on his
 * permissions they will be able to set the values from some or all of the features.
 */
export class ThemePanel extends VssComponent<ThemePanelProps, IThemePanelState> {
    constructor(props: ThemePanelProps, context: IVssComponentContext) {
        super(props, context);

        this.state = {};
    }

    public render(): JSX.Element {
        const { currentThemeId } = this.state;

        const themeGroups = this.state.themeGroups || {};
        const themeGroupNames = Object.keys(themeGroups);

        return (
            <Panel
                size={ContentSize.Medium}
                lightDismiss={true}
                className="theme-panel"
                titleProps={{ text: ThemeResources.ThemePanelHeaderText }}
                onDismiss={this._closePanel}
                modal={false}
            >
                {currentThemeId && themeGroupNames.length > 0 && (
                    <FocusZone
                        defaultActiveElement={`#theme-${sanitizeThemeId(currentThemeId)}`}
                        direction={FocusZoneDirection.Horizontal}
                        focusOnMount={true}
                        focusGroupProps={{ defaultElementId: `theme-${sanitizeThemeId(currentThemeId)}` }}
                    >
                        <div>
                            {themeGroupNames.map(groupName => {
                                return (
                                    <div className="theme-management-group" key={groupName}>
                                        {themeGroupNames.length > 1 && <p>{groupName}</p>}
                                        <div className="theme-management-container flex-row">
                                            {themeGroups[groupName].map(theme => (
                                                <ThemeTile
                                                    key={theme.id}
                                                    selected={theme.id === currentThemeId}
                                                    theme={theme}
                                                    onThemeSelected={this._onThemeSelected}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                            {this.state.additionalThemesUrl && (
                                <Link aria-label={ThemeResources.BrowseMoreLink} href={this.state.additionalThemesUrl} target="_blank">
                                    {ThemeResources.BrowseMoreLink}
                                </Link>
                            )}
                        </div>
                    </FocusZone>
                )}
            </Panel>
        );
    }

    public componentDidMount(): void {
        super.componentDidMount();

        this.observe([{ propertyName: "themeService", observer: this._onSelectedThemeChanged, action: "selectedThemeChanged" }]);

        this.props.themeService.getThemes().then(themes => {
            this.setThemes(themes);
        });
        this.props.themeService.getCurrentThemeId().then(currentThemeId => {
            this.setState({ currentThemeId });
        });

        // Re-enable this once we support third-party theme extensions
        // this.props.themeService.getBrowseMoreUrl().then(browseUrl => {
        //    this.setState({ additionalThemesUrl: browseUrl })
        // });
    }

    private setThemes(themes: ClientTheme[]): void {
        const themeGroups: { [groupName: string]: ClientTheme[] } = {};

        themes = this.addThemesToGroup(
            themes,
            themeGroups,
            ThemeResources.DefaultThemesText,
            t => t.id === "ms.vss-web.vsts-theme-dark" || t.id === "ms.vss-web.vsts-theme" || t.id === "ms.vss-web.vsts-theme-hc-dark" || t.id === "ms.vss-web.vsts-theme-hc-light"
        );
        themes = this.addThemesToGroup(themes, themeGroups, ThemeResources.CustomThemesText, t => !t.isPreview);
        themes = this.addThemesToGroup(themes, themeGroups, ThemeResources.PreviewThemesText, t => !!t.isPreview);

        this.setState({ themeGroups });
    }

    private addThemesToGroup(
        themes: ClientTheme[],
        themeGroups: { [groupName: string]: ClientTheme[] },
        groupName: string,
        filter: (theme: ClientTheme) => boolean
    ): ClientTheme[] {
        const remainingThemes: ClientTheme[] = [];
        const matchingThemes: ClientTheme[] = [];

        for (const theme of themes) {
            if (filter(theme)) {
                matchingThemes.push(theme);
            } else {
                remainingThemes.push(theme);
            }
        }

        if (matchingThemes.length) {
            themeGroups[groupName] = matchingThemes;
        }

        return remainingThemes;
    }

    private _closePanel = (): void => {
        if (this.props.onDismiss) {
            this.props.onDismiss();
        }
    };

    private _onSelectedThemeChanged = (themeChangedEvent: IThemeChangedEvent) => {
        this.setState({ currentThemeId: themeChangedEvent.selectedTheme.id });
    };

    private _onThemeSelected = (theme: ClientTheme): void => {
        this.props.themeService.setTheme(theme);
    };
}

interface ThemeTileProps {
    theme: ClientTheme;
    selected: boolean;
    onThemeSelected: (theme: ClientTheme) => void;
}

const ThemeTile = (props: ThemeTileProps): JSX.Element => {
    const { onThemeSelected, selected, theme } = props;

    const headerBackground = expandThemeableProperties("var(--nav-header-background)", theme.data);
    const headerProductColor = expandThemeableProperties("var(--nav-header-product-color)", theme.data);
    const verticalNav = expandThemeableProperties("var(--nav-vertical-background-color)", theme.data);
    const backgroundColor = expandThemeableProperties("var(--background-color)", theme.data);
    const primaryText = expandThemeableProperties("var(--text-primary-color)", theme.data);
    const communication = expandThemeableProperties("rgba(var(--palette-primary), 1)", theme.data);

    return (
        <FocusGroupContext.Consumer>
            {focusGroupContext => (
                <FocusZoneContext.Consumer>
                    {focusZoneContext => {
                        const id = `theme-${sanitizeThemeId(theme.id)}`;
                        return (
                            <div
                                aria-label={theme.name}
                                onClick={() => {
                                    onThemeSelected(theme);
                                }}
                                onFocus={() => focusGroupContext.onFocus(id)}
                                onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                                    const which = e.which;
                                    if (which === KeyCode.enter || which === KeyCode.space) {
                                        onThemeSelected(theme);
                                    }
                                }}
                                className={css("theme-tile-container", selected && "selected")}
                                data-focuszone={focusZoneContext.focuszoneId}
                                id={id}
                                tabIndex={getTabIndex({ id: id }, focusGroupContext)}
                            >
                                <div className="theme-tile">
                                    <div
                                        className="theme-tile-sections"
                                        style={{ backgroundColor: backgroundColor, borderColor: `rgba(${primaryText}, 0.3)` }}
                                    >
                                        <div
                                            className="theme-header"
                                            style={{ background: headerBackground, boxShadow: primaryText + " 0px 1px 0px", zIndex: 1 }}
                                        >
                                            <div className="theme-button" style={{ backgroundColor: headerProductColor }} />
                                        </div>
                                        <div className="theme-nav" style={{ backgroundColor: verticalNav }} />
                                        <div className="theme-background" style={{ backgroundColor: backgroundColor }}>
                                            <div className="theme-text long" style={{ backgroundColor: primaryText }} />
                                            <div className="theme-text" style={{ backgroundColor: primaryText }} />
                                            <div className="theme-text short" style={{ backgroundColor: primaryText }} />
                                            <div className="theme-text long" style={{ backgroundColor: primaryText }} />
                                            <div className="theme-button wide" style={{ backgroundColor: communication }} />
                                        </div>
                                    </div>
                                    <div className="theme-name">{theme.name}</div>
                                </div>
                            </div>
                        );
                    }}
                </FocusZoneContext.Consumer>
            )}
        </FocusGroupContext.Consumer>
    );
};
